import {
  Box,
  useTheme,
  Typography,
  Button,
  IconButton,
  Tooltip,
  TextField,
  Checkbox,
  Menu,
  MenuItem,
  Divider,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import durationPlugin from "dayjs/plugin/duration";
import { useStateContext } from "contexts/ContextProvider";
import React, { useCallback, useEffect, useState } from "react";
import Grid from "@mui/material/Grid2";
import api from "services/api";
import { useWebSocket } from "contexts/WebSocketContext";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import { useAuth } from "../../../contexts/AuthContext";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import SortRoundedIcon from "@mui/icons-material/SortRounded";
import { usePageTitle } from "hooks/usePageTitle";
import { formatAmount } from "helpers/ServiceHelpers";
import { useEditLock } from "hooks/useEditLock";
import EditLockDialog from "components/EditLockDialog";

// Sort menu options — the six visible columns plus extra fields not shown as
// columns (Language is required; Service Category is a trivial string field).
const SORT_MENU_OPTIONS = [
  { key: "Project", label: "Project Number" },
  { key: "Date", label: "Date" },
  { key: "Details", label: "Details" },
  { key: "Unit", label: "Unit" },
  { key: "Rate", label: "Rate" },
  { key: "Total", label: "Total" },
  { key: "Language", label: "Language" },
  { key: "ServiceCategory", label: "Service Category" },
];

const InvoiceEdit = () => {
  const { currentUser } = useAuth();

  const theme = useTheme();

  const normalizeAddressPart = (value) => {
    const normalized = String(value || "").trim();
    if (!normalized || normalized.toUpperCase() === "N/A") return null;
    return normalized;
  };

  const splitLegacyAddress = (value) => {
    const normalized = (value || "").trim();
    if (!normalized) {
      return { line1: "", line2: "", line3: "" };
    }

    const newlineParts = normalized
      .split(/\r?\n/)
      .map((part) => normalizeAddressPart(part))
      .filter(Boolean);

    if (newlineParts.length >= 3) {
      return {
        line1: newlineParts[0],
        line2: newlineParts.slice(1, -1).join(", "),
        line3: newlineParts[newlineParts.length - 1],
      };
    }

    if (newlineParts.length === 2) {
      return {
        line1: newlineParts[0],
        line2: "",
        line3: newlineParts[1],
      };
    }

    const commaParts = normalized
      .split(",")
      .map((part) => normalizeAddressPart(part))
      .filter(Boolean);

    if (commaParts.length >= 3) {
      return {
        line1: commaParts[0],
        line2: commaParts.slice(1, -1).join(", "),
        line3: commaParts[commaParts.length - 1],
      };
    }

    if (commaParts.length === 2) {
      return {
        line1: commaParts[0],
        line2: "",
        line3: commaParts[1],
      };
    }

    return {
      line1: normalizeAddressPart(normalized) || "",
      line2: "",
      line3: "",
    };
  };

  // Convert duration string (HH:MM:SS) to decimal hours with proper formatting
  const convertDurationToDecimal = (durationString) => {
    if (!durationString) return "0 hours";

    const parts = durationString.split(":");
    if (parts.length === 3) {
      const hours = parseInt(parts[0]) || 0;
      const minutes = parseInt(parts[1]) || 0;
      const seconds = parseInt(parts[2]) || 0;
      const decimalHours = hours + minutes / 60 + seconds / 3600;

      // Round to 2 decimals first
      const rounded = Math.round(decimalHours * 100) / 100;

      // Format: show as integer if no decimal part, otherwise with 2 decimals
      let formattedHours;
      if (rounded % 1 === 0) {
        formattedHours = Math.floor(rounded);
      } else {
        formattedHours = rounded.toFixed(2);
      }

      // Return with proper singular/plural
      const unit = parseFloat(formattedHours) === 1 ? "hour" : "hours";
      return `${formattedHours} ${unit}`;
    }
    return durationString;
  };

  // Parse duration string (HH:MM:SS) to numeric hours for sorting
  const parseDurationToHours = (durationString) => {
    if (!durationString) return null;
    const parts = durationString.split(":");
    if (parts.length === 3) {
      const hours = parseInt(parts[0]) || 0;
      const minutes = parseInt(parts[1]) || 0;
      const seconds = parseInt(parts[2]) || 0;
      return hours + minutes / 60 + seconds / 3600;
    }
    return null;
  };

  // Resolve the numeric rate the Rate cell displays
  const resolveServiceRate = (item) => {
    if (item.ServiceCategory === "Interpretation") {
      return item.ServiceClientRate !== null &&
        item.ServiceClientRate !== undefined
        ? item.ServiceClientRate
        : item.Client?.ClientRates?.[0]?.Rate;
    }
    return item.Rate;
  };

  // Resolve the sort value for a column — mirrors the cell display logic exactly
  const getSortValue = (item, key) => {
    switch (key) {
      case "Project": {
        const v = String(item.ProjectNumber || "").trim().toLowerCase();
        return v || null;
      }
      case "Date": {
        const d = item.Date ? dayjs(item.Date) : null;
        return d && d.isValid() ? d.valueOf() : null;
      }
      case "Details": {
        const v = String(item.Details || "").trim().toLowerCase();
        return v || null;
      }
      case "Unit": {
        if (item.ServiceCategory === "Interpretation") {
          return parseDurationToHours(item.TotalDuration);
        }
        if (item.UnitCount && Number(item.UnitCount) > 0) {
          return Number(item.UnitCount);
        }
        if (item.WordCount && item.WordCount > 0) return Number(item.WordCount);
        return null;
      }
      case "Rate": {
        const rate = resolveServiceRate(item);
        if (rate === 0 || rate === null || rate === undefined) return null;
        return Number(rate);
      }
      case "Total": {
        const charge =
          typeof item.AccountCharge === "number"
            ? item.AccountCharge
            : parseFloat(item.AccountCharge);
        return isNaN(charge) ? null : charge;
      }
      case "Language": {
        const langName =
          item.ServiceTargetLanguages?.[0]?.Language?.Name ??
          item.Language?.Name;
        const v = String(langName || "").trim().toLowerCase();
        return v || null;
      }
      case "ServiceCategory": {
        const v = String(item.ServiceCategory || "").trim().toLowerCase();
        return v || null;
      }
      default:
        return null;
    }
  };

  // Comparator: iterate criteria in priority order (index 0 = primary).
  // Missing/empty values always sort last for their criterion regardless of
  // direction. React's Array.prototype.sort is stable, so equal keys keep
  // their current order — which is what makes appended secondary criteria
  // behave correctly.
  const compareServices = (a, b, criteria) => {
    for (const config of criteria) {
      const va = getSortValue(a, config.key);
      const vb = getSortValue(b, config.key);
      if (va === null && vb === null) continue;
      if (va === null) return 1;
      if (vb === null) return -1;
      const cmp = va < vb ? -1 : va > vb ? 1 : 0;
      if (cmp !== 0) return config.direction === "asc" ? cmp : -cmp;
    }
    return 0;
  };

  const {
    overlayData,
    setOverlayName,
    triggerPageRefresh,
    openDialog,
    handleErrorDialog,
    closeDialog,
  } = useStateContext();

  const { socket } = useWebSocket();

  usePageTitle("Edit Invoice");

  // Edit lock for existing invoices only
  const { isLocked, lockedBy, releaseLock } = useEditLock(
    "invoice",
    overlayData?.invoiceData?.id || null,
  );

  // Get invoice data from overlayData
  const invoiceData = overlayData?.invoiceData || {};

  const invoiceNumber = invoiceData.Number || "";
  const [invoiceDate, setInvoiceDate] = useState(
    invoiceData.Date ? dayjs(invoiceData.Date) : dayjs(),
  );
  const [invoiceDueDate, setInvoiceDueDate] = useState(
    invoiceData.DueDate ? dayjs(invoiceData.DueDate) : dayjs().add(30, "day"),
  );
  const [invoiceNote, setInvoiceNote] = useState(invoiceData.Note || "");
  const [purchaseOrder, setPurchaseOrder] = useState(
    invoiceData.PurchaseOrder || "",
  );
  const [clientName, setClientName] = useState(
    invoiceData.ClientName || invoiceData.Client?.Account || "",
  );
  const clientAddressRecord = invoiceData.Client?.AddressRecord;
  const hasStructuredClientAddress = [
    clientAddressRecord?.AddressLine1,
    clientAddressRecord?.AddressLine2,
    clientAddressRecord?.AddressLine3,
  ].some((part) => typeof part === "string" && part.trim() !== "");
  // The invoice keeps its OWN address snapshot (Address TEXT, newline-joined).
  // Prefer it over the client's current address so the form edits the invoice's
  // copy — the invoice must not track the client's live AddressRecord.
  const invoiceOwnAddress = String(invoiceData.Address || "").trim();
  const hasInvoiceOwnAddress =
    invoiceOwnAddress && invoiceOwnAddress.toUpperCase() !== "N/A";
  const initialAddress = hasInvoiceOwnAddress
    ? splitLegacyAddress(invoiceOwnAddress)
    : hasStructuredClientAddress
      ? {
          line1: normalizeAddressPart(clientAddressRecord?.AddressLine1) || "",
          line2: normalizeAddressPart(clientAddressRecord?.AddressLine2) || "",
          line3: normalizeAddressPart(clientAddressRecord?.AddressLine3) || "",
        }
      : splitLegacyAddress(invoiceData.Client?.MainAddress || "");
  const [addressLine1, setAddressLine1] = useState(initialAddress.line1);
  const [addressLine2, setAddressLine2] = useState(initialAddress.line2);
  const [addressLine3, setAddressLine3] = useState(initialAddress.line3);
  const [paymentLink, setPaymentLink] = useState(invoiceData.PaymentLink || "");

  // Discount fields state
  const [discountAmount, setDiscountAmount] = useState(
    invoiceData.DiscountAmount !== undefined
      ? parseFloat(invoiceData.DiscountAmount) || 0
      : 0,
  );
  const [discountPercentage, setDiscountPercentage] = useState(
    invoiceData.DiscountPercentage !== undefined
      ? parseFloat(invoiceData.DiscountPercentage) || 0
      : null,
  );
  const [discountType, setDiscountType] = useState(
    invoiceData.DiscountType || "FIXED",
  );
  const [discountNote, setDiscountNote] = useState(
    invoiceData.DiscountNote || "",
  );

  // Calculate discounted total in real-time
  const calculateDiscountedTotal = () => {
    if (!discountAmount && discountPercentage === null) return 0;

    let discountValue = 0;
    if (discountType === "PERCENTAGE" && discountPercentage !== null) {
      discountValue = invoiceData.Amount * (discountPercentage / 100);
    } else {
      // FIXED type
      discountValue = parseFloat(discountAmount) || 0;
    }

    return Math.max(0, invoiceData.Amount - discountValue);
  };

  const discountedTotal = calculateDiscountedTotal();

  const invoiceClient = invoiceData.Client || null;
  const [invoiceServices, setInvoiceServices] = useState(
    (invoiceData.Services || []).map((service) => ({
      ...service,
      OriginalDetails: service.Details,
      OriginalServiceClientRate: service.ServiceClientRate,
    })),
  );

  // Unit types (the new Unit system: UnitCount + UnitTypeID → "330 Pages").
  // InvoiceProfile already fetches /unitTypes; InvoiceEdit needs the same
  // lookup to render the Unit column for non-interpretation services.
  const [unitTypes, setUnitTypes] = useState([]);

  useEffect(() => {
    let active = true;
    api
      .get("/unitTypes")
      .then((response) => {
        if (active) setUnitTypes(Array.isArray(response?.data) ? response.data : []);
      })
      .catch((error) => {
        console.error("Error fetching unit types:", error);
        if (active) setUnitTypes([]);
      });
    return () => {
      active = false;
    };
  }, []);

  const [isSavingChanges, setIsSavingChanges] = useState(false);

  dayjs.extend(durationPlugin);

  const updateServiceInvoice = async (service, invoiceData) => {
    try {
      // Call the new simplified invoice update endpoint
      const response = await api.put(`/services/${service.id}/invoice`, {
        InvoiceID: invoiceData.id,
        Details: service.Details,
        ServiceClientRate:
          service.ServiceClientRate !== undefined
            ? service.ServiceClientRate
            : null, // Include custom rate if set
      });

      return response.data;
    } catch (error) {
      console.error("Error updating service invoice:", error);
      throw error;
    }
  };

  const removeServiceFromInvoice = async (service) => {
    try {
      // Call endpoint to clear the invoice ID
      const response = await api.put(`/services/${service.id}/invoice/remove`);
      return response.data;
    } catch (error) {
      console.error("Error removing service from invoice:", error);
      throw error;
    }
  };

  // Edit details state
  const [editingItemIndex, setEditingItemIndex] = useState(null);
  const [editDetailsText, setEditDetailsText] = useState("");
  const [editRateText, setEditRateText] = useState("");

  // Multi-level sort criteria — ordered list of { key, direction }, index 0 is
  // the primary criterion. Empty array = no active sort (manual order is
  // authoritative after a manual move).
  const [sortCriteria, setSortCriteria] = useState([]);

  // Re-sort invoiceServices IN PLACE whenever the criteria change, so the
  // existing index-based edit logic stays valid (the sorted order IS the saved
  // order). No loop hazard: this effect only depends on sortCriteria, and
  // setInvoiceServices never changes it. No-op when criteria are empty.
  useEffect(() => {
    if (sortCriteria.length === 0) return;
    setInvoiceServices((prev) =>
      [...prev].sort((a, b) => compareServices(a, b, sortCriteria)),
    );
  }, [sortCriteria]);

  // Apply a new criteria list; cancels any in-progress row edit so index-based
  // edit state can't apply to the wrong row after a reorder.
  const applySortCriteria = (nextCriteria) => {
    setSortCriteria(nextCriteria);
    if (editingItemIndex !== null) {
      setEditingItemIndex(null);
      setEditDetailsText("");
      setEditRateText("");
    }
  };

  // Header/menu click: not a criterion yet → append as lowest-priority asc;
  // already a criterion → cycle its direction asc → desc → asc.
  const handleSort = (key) => {
    const nextCriteria = [...sortCriteria];
    const existingIndex = nextCriteria.findIndex((c) => c.key === key);
    if (existingIndex === -1) {
      nextCriteria.push({ key, direction: "asc" });
    } else {
      const existing = nextCriteria[existingIndex];
      nextCriteria[existingIndex] = {
        ...existing,
        direction: existing.direction === "asc" ? "desc" : "asc",
      };
    }
    applySortCriteria(nextCriteria);
  };

  // Manual move up/down — overrides any active sort (clears ALL criteria)
  const moveService = (index, direction) => {
    setSortCriteria([]);
    if (editingItemIndex !== null) {
      setEditingItemIndex(null);
      setEditDetailsText("");
      setEditRateText("");
    }
    setInvoiceServices((prev) => {
      const target = index + direction;
      if (target < 0 || target >= prev.length) return prev;
      const updated = [...prev];
      const [moved] = updated.splice(index, 1);
      updated.splice(target, 0, moved);
      return updated;
    });
  };

  // Sortable header cell for the Projects table. Shows the direction arrow for
  // any active criterion on this column, plus a tiny priority number when more
  // than one criterion is active (so Language-only sorts and multi-key states
  // are understandable).
  const renderSortableHeader = (label, sortKey) => {
    const criterionIndex = sortCriteria.findIndex((c) => c.key === sortKey);
    const active = criterionIndex !== -1;
    const criterion = active ? sortCriteria[criterionIndex] : null;
    return (
      <Box
        key={sortKey}
        onClick={() => handleSort(sortKey)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.25,
          cursor: "pointer",
          userSelect: "none",
          "&:hover": { opacity: 0.75 },
        }}
      >
        <Typography variant="h6" color={theme.palette.primary.main}>
          {label}
        </Typography>
        {active ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
            {criterion.direction === "asc" ? (
              <ArrowUpwardRoundedIcon sx={{ fontSize: 16 }} />
            ) : (
              <ArrowDownwardRoundedIcon sx={{ fontSize: 16 }} />
            )}
            {sortCriteria.length > 1 ? (
              <Typography
                variant="caption"
                sx={{ fontSize: 10, lineHeight: 1, fontWeight: 700 }}
              >
                {criterionIndex + 1}
              </Typography>
            ) : null}
          </Box>
        ) : null}
      </Box>
    );
  };

  // Sort menu state (anchor for the sort-options Menu next to Add Service)
  const [sortMenuAnchorEl, setSortMenuAnchorEl] = useState(null);
  const isSortMenuOpen = Boolean(sortMenuAnchorEl);
  const handleSortMenuOpen = (event) =>
    setSortMenuAnchorEl(event.currentTarget);
  const handleSortMenuClose = () => setSortMenuAnchorEl(null);

  // Add service dialog state
  const [availableServices, setAvailableServices] = useState([]);
  const [selectedNewServices, setSelectedNewServices] = useState([]);
  const [isServiceDialogOpen, setIsServiceDialogOpen] = useState(false);
  const [deletedServices, setDeletedServices] = useState([]);

  // Keep Due Date in sync: always 30 days after invoiceDate
  useEffect(() => {
    if (!invoiceDate) return;
    setInvoiceDueDate(invoiceDate.add(30, "day"));
  }, [invoiceDate]);

  // Fetch available services for adding to invoice
  const fetchAvailableServices = async () => {
    if (!invoiceClient) return;

    try {
      const response = await api.get("/services/invoices", {
        params: {
          clientId: invoiceClient.id,
          invoiced: "false", // Only non-invoiced services
        },
      });

      // Store all services without filtering - we'll filter dynamically in the dialog
      setAvailableServices(response.data);
    } catch (error) {
      console.error("Error fetching available services:", error);
      setAvailableServices([]);
    }
  };

  // Open dialog to add services to invoice
  const openAddServiceDialog = () => {
    setIsServiceDialogOpen(true);
  };

  const handleAddSelectedServices = useCallback(() => {
    // Combine availableServices and deletedServices for lookup
    const allServices = [...availableServices, ...deletedServices];

    const newItems = selectedNewServices.map((serviceId) => {
      const service = allServices.find((s) => s.id === serviceId);
      return {
        id: service.id,
        ProjectNumber: service.ProjectNumber,
        Date: service.Date,
        Details: service.Details,
        OriginalDetails: service.Details,
        OriginalServiceClientRate: service.ServiceClientRate,
        ServiceUnit: service.ServiceUnit,
        ServiceClientRate: service.ServiceClientRate,
        AccountCharge: service.AccountCharge || 0,
        isNewlyAdded: true,
        ...service,
      };
    });

    setInvoiceServices((prev) => [...prev, ...newItems]);
    setDeletedServices((prev) =>
      prev.filter((s) => !selectedNewServices.includes(s.id)),
    );
    closeDialog();
    setSelectedNewServices([]);
  }, [availableServices, deletedServices, selectedNewServices, closeDialog]);

  // Effect to update dialog when selection changes
  useEffect(() => {
    if (!isServiceDialogOpen) return;

    const combinedServices = [...availableServices, ...deletedServices];

    // Remove duplicates by id
    const uniqueServices = combinedServices.filter(
      (service, index, self) =>
        index === self.findIndex((s) => s.id === service.id),
    );

    // Filter out services already in the invoice
    const existingServiceIds = new Set(
      (invoiceServices || []).map((item) => item.id),
    );
    const filteredServices = uniqueServices.filter(
      (service) => !existingServiceIds.has(service.id),
    );

    const content =
      filteredServices.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
          No available services found for this client. All completed services
          may already be invoiced.
        </Typography>
      ) : (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select services to add to this invoice ({filteredServices.length}{" "}
            available)
          </Typography>

          {/* Service List */}
          <Box sx={{ maxHeight: "400px", overflowY: "auto" }}>
            {(() => {
              // Group services by month/year
              const grouped = filteredServices.reduce((acc, service) => {
                if (!service.Date) return acc;
                const monthYear = dayjs(service.Date).format("MMMM YYYY");
                if (!acc[monthYear]) {
                  acc[monthYear] = [];
                }
                acc[monthYear].push(service);
                return acc;
              }, {});

              // Sort by date descending (most recent first)
              const sortedMonths = Object.keys(grouped).sort((a, b) => {
                return (
                  dayjs(b, "MMMM YYYY").valueOf() -
                  dayjs(a, "MMMM YYYY").valueOf()
                );
              });

              return sortedMonths.map((monthYear) => (
                <Box
                  key={monthYear}
                  sx={{
                    mb: 2,
                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                    pb: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 1.5,
                      fontWeight: 600,
                      color: theme.palette.primary.main,
                    }}
                  >
                    {monthYear}
                  </Typography>
                  {grouped[monthYear].map((service) => (
                    <Box
                      key={service.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        p: 1.5,
                        mb: 1,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: "8px",
                        cursor: "pointer",
                        bgcolor: selectedNewServices.includes(service.id)
                          ? theme.palette.action.selected
                          : "transparent",
                        "&:hover": {
                          bgcolor: theme.palette.action.hover,
                        },
                      }}
                      onClick={() => {
                        setSelectedNewServices((prev) =>
                          prev.includes(service.id)
                            ? prev.filter((id) => id !== service.id)
                            : [...prev, service.id],
                        );
                      }}
                    >
                      <Checkbox
                        checked={selectedNewServices.includes(service.id)}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => {
                          setSelectedNewServices((prev) =>
                            prev.includes(service.id)
                              ? prev.filter((id) => id !== service.id)
                              : [...prev, service.id],
                          );
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" fontWeight={500}>
                          Project #: {service.ProjectNumber}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {service.Details || "No details"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {service.Date
                            ? dayjs(service.Date).format("MM/DD/YYYY")
                            : "No date"}
                        </Typography>
                      </Box>
                      <Typography variant="body1" fontWeight={500}>
                        $
                        {typeof service.AccountCharge === "number"
                          ? service.AccountCharge.toFixed(2)
                          : (parseFloat(service.AccountCharge) || 0).toFixed(2)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ));
            })()}
          </Box>
        </Box>
      );

    openDialog({
      theme: theme,
      title: `Add Services to Invoice - Client: ${
        invoiceClient?.Account || "N/A"
      }`,
      message: null,
      customContent: content,
      secondaryAction: "Cancel",
      primaryAction:
        filteredServices.length > 0 && selectedNewServices.length > 0
          ? `Add (${selectedNewServices.length}) Selected`
          : null,
      secondaryOnClick: () => {
        closeDialog();
        setIsServiceDialogOpen(false);
        setSelectedNewServices([]);
      },
      primaryOnClick:
        filteredServices.length > 0 && selectedNewServices.length > 0
          ? () => {
              handleAddSelectedServices();
              setIsServiceDialogOpen(false);
            }
          : null,
    });
  }, [
    isServiceDialogOpen,
    selectedNewServices,
    availableServices,
    invoiceServices,
    deletedServices,
    closeDialog,
    handleAddSelectedServices,
    invoiceClient?.Account,
    openDialog,
    theme,
  ]);

  const saveInvoices = async (event) => {
    if (event && event.preventDefault) {
      event.preventDefault();
    }

    if (!invoiceServices || invoiceServices.length === 0) {
      return;
    }

    setIsSavingChanges(true);

    try {
      if (invoiceData.id) {
        // UPDATE existing invoice
        const normalizedAddressLine1 = normalizeAddressPart(addressLine1);
        const normalizedAddressLine2 = normalizeAddressPart(addressLine2);
        const normalizedAddressLine3 = normalizeAddressPart(addressLine3);

        const updatedInvoiceData = {
          Date: invoiceDate.format("YYYY-MM-DD"),
          DueDate: invoiceDueDate.format("YYYY-MM-DD"),
          ClientName: clientName || null,
          Address:
            [
              normalizedAddressLine1,
              normalizedAddressLine2,
              normalizedAddressLine3,
            ]
              .filter(Boolean)
              .join("\n") || null,
          AddressLine1: normalizedAddressLine1,
          AddressLine2: normalizedAddressLine2,
          AddressLine3: normalizedAddressLine3,
          Note: invoiceNote || null,
          PurchaseOrder: purchaseOrder || null,
          PaymentLink: paymentLink || null,
          Amount: invoiceServices.reduce(
            (sum, service) => sum + (parseFloat(service.AccountCharge) || 0),
            0,
          ),
          // Discount fields
          DiscountAmount: discountAmount !== undefined ? discountAmount : null,
          DiscountPercentage:
            discountPercentage !== undefined && discountPercentage > 0
              ? discountPercentage
              : null,
          DiscountType: discountType,
          DiscountNote: discountNote || null,
          // Persist the current service order (header sort or manual moves)
          serviceIdsOrder: invoiceServices.map((s) => s.id).filter(Boolean),
        };

        await api.put(`/invoices/${invoiceData.id}`, updatedInvoiceData);

        // Update services linked to this invoice - handle both modified and newly added services
        if (invoiceServices.length > 0) {
          const servicesToUpdate = invoiceServices.filter((service) => {
            // Include newly added services
            if (service.isNewlyAdded) {
              return true;
            }

            // Check if Details changed
            const detailsChanged = service.Details !== service.OriginalDetails;
            // Check if ServiceClientRate changed (comparing with original or checking if it was set)
            const rateChanged =
              service.ServiceClientRate !== undefined &&
              service.ServiceClientRate !== service.OriginalServiceClientRate;

            return detailsChanged || rateChanged;
          });

          if (servicesToUpdate.length > 0) {
            await Promise.all(
              servicesToUpdate.map((service) =>
                updateServiceInvoice(service, invoiceData),
              ),
            );
          }
        }

        // Remove deleted services from invoice (set InvoiceID to null)
        if (deletedServices.length > 0) {
          await Promise.all(
            deletedServices.map((service) => removeServiceFromInvoice(service)),
          );
        }

        // Emit socket events for real-time updates
        if (socket) {
          // Notify profile viewers of this invoice
          socket.emit("entity:saved", {
            modelType: "Invoice",
            recordId: invoiceData.id,
            user: {
              id: currentUser?.id,
              name: currentUser?.Name || "User",
              imageThumbnail: currentUser?.ImageThumbnail,
            },
          });
          // Notify list viewers
          socket.emit("entity:saved", {
            modelType: "Invoice",
            recordId: "all",
            user: {
              id: currentUser?.id,
              name: currentUser?.Name || "User",
              imageThumbnail: currentUser?.ImageThumbnail,
            },
          });
        }

        // Show success message and close overlay without page refresh
        setOverlayName("");

        // Keep the edit dialog open so user can see updated data immediately
        // No triggerPageRefresh needed - socket updates will handle UI consistency
      }
    } catch (error) {
      console.error("Error saving invoice:", error);
      const message =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to save invoice. Please try again.";
      handleErrorDialog(message, theme);
    } finally {
      setIsSavingChanges(false);
    }
  };

  return (
    <Box
      bgcolor="#fff"
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        bgcolor: "rgba(0, 0, 0, 0.8)",
        zIndex: 1300,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          bgcolor: theme.palette.background.alt,
          width: "80%",
          height: "85%",
          minWidth: "600px",
          maxWidth: "1400px",
          display: "flex",
          borderRadius: "1.5rem",
          justifyContent: "center",
          alignItems: "flex-start",
          flexDirection: "column",
          color: theme.palette.text.primary,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            width: "100%",
            flexDirection: "column",
            p: ".75rem 2rem",
            gap: 2,
          }}
        >
          {/* Header row with title and menu */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <IconButton
                color="primary"
                size="large"
                onClick={() => setOverlayName("")}
              >
                <ChevronLeftRoundedIcon fontSize="inherit" />
              </IconButton>

              <Typography variant="h2" fontWeight="bold">
                Edit Invoice #{invoiceNumber}
              </Typography>
            </Box>
          </Box>

          {/* Invoice Edit Fields */}
          <Grid container spacing={2} sx={{ width: "100%" }}>
            {/* Row 1: Name, Date, Due Date */}
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                label="Client Name"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <DatePicker
                label="Date"
                value={invoiceDate}
                onChange={(newValue) => setInvoiceDate(newValue)}
                slotProps={{ textField: { size: "small", fullWidth: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                label="Due Date (auto 30 days)"
                value={invoiceDueDate.format("MM/DD/YYYY")}
                InputLabelProps={{ shrink: true }}
                size="small"
                fullWidth
                InputProps={{ readOnly: true }}
              />
            </Grid>

            {/* Row 2: Address lines */}
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                label="Address Line 1"
                value={addressLine1}
                onChange={(e) => setAddressLine1(e.target.value)}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                label="Address Line 2 (Optional)"
                value={addressLine2}
                onChange={(e) => setAddressLine2(e.target.value)}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                label="City, State ZIP"
                value={addressLine3}
                onChange={(e) => setAddressLine3(e.target.value)}
                size="small"
                fullWidth
              />
            </Grid>

            {/* Row 3: Note and PO */}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Invoice Note"
                value={invoiceNote}
                onChange={(e) => setInvoiceNote(e.target.value)}
                multiline
                rows={2}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Purchase Order"
                value={purchaseOrder}
                onChange={(e) => setPurchaseOrder(e.target.value)}
                multiline
                rows={2}
                size="small"
                fullWidth
              />
            </Grid>

            {/* Row 4: Payment Link */}
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Payment Link"
                value={paymentLink}
                onChange={(e) => setPaymentLink(e.target.value)}
                size="small"
                fullWidth
              />
            </Grid>

            {/* Row 5: Discount Fields */}
            <Grid container spacing={2} sx={{ mt: 1 }} size={{ xs: 12 }}>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  label="Discount Amount"
                  value={discountAmount}
                  onChange={(e) => {
                    // Allow any numeric input including decimals
                    const rawValue = e.target.value;
                    if (rawValue === "" || rawValue === ".") {
                      setDiscountAmount(0);
                    } else {
                      // Parse as float, allowing negative values for editing
                      let parsed = parseFloat(rawValue);
                      if (!isNaN(parsed)) {
                        setDiscountAmount(parsed);
                      }
                    }
                  }}
                  size="small"
                  fullWidth
                  inputProps={{ type: "number", min: "0" }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  label="Discount Percentage (Optional)"
                  value={discountPercentage !== null ? discountPercentage : ""}
                  onChange={(e) => {
                    const value =
                      e.target.value === "" ? null : parseFloat(e.target.value);
                    setDiscountPercentage(value || null);
                  }}
                  size="small"
                  fullWidth
                  inputProps={{ step: "0.1", min: "0", max: "100" }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  select
                  label="Discount Type"
                  value={discountType}
                  onChange={(e) => setDiscountType(e.target.value)}
                  size="small"
                  fullWidth
                >
                  <MenuItem value="FIXED">Fixed Amount</MenuItem>
                  <MenuItem value="PERCENTAGE">Percentage (%)</MenuItem>
                </TextField>
              </Grid>
              <Grid
                size={{ xs: 12, md: 4 }}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  width: "100%",
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Discounted Total:
                </Typography>
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  color={discountedTotal >= 0 ? "primary.main" : "error.main"}
                >
                  ${discountedTotal.toFixed(2)}
                </Typography>
              </Grid>
              <Grid size={{ xs: 8 }}>
                <TextField
                  label="Discount Note / Remark"
                  value={discountNote}
                  onChange={(e) => setDiscountNote(e.target.value)}
                  multiline
                  rows={2}
                  placeholder="Optional: Explain why this discount was applied..."
                  size="small"
                  fullWidth
                />
              </Grid>
            </Grid>
          </Grid>
        </Box>

        {/* Invoice Items Section */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            p: "1rem 2rem",
            width: "100%",
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              mb: 1.5,
            }}
          >
            <Typography variant="h4" fontWeight="bold">
              Projects
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Tooltip title="Sort Projects">
                <IconButton
                  color="primary"
                  size="large"
                  onClick={handleSortMenuOpen}
                >
                  <SortRoundedIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Add Service">
                <IconButton
                  color="primary"
                  size="large"
                  onClick={async () => {
                    await fetchAvailableServices();
                    openAddServiceDialog();
                  }}
                >
                  <AddRoundedIcon />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={sortMenuAnchorEl}
                open={isSortMenuOpen}
                onClose={handleSortMenuClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                {SORT_MENU_OPTIONS.map((opt) => {
                  const criterionIndex = sortCriteria.findIndex(
                    (c) => c.key === opt.key,
                  );
                  const active = criterionIndex !== -1;
                  const criterion = active
                    ? sortCriteria[criterionIndex]
                    : null;
                  return (
                    <MenuItem
                      key={opt.key}
                      dense
                      onClick={() => {
                        handleSortMenuClose();
                        handleSort(opt.key);
                      }}
                      sx={{ gap: 1, minWidth: 220 }}
                    >
                      <Box
                        sx={{
                          width: 18,
                          flexShrink: 0,
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        {active ? (
                          criterion.direction === "asc" ? (
                            <ArrowUpwardRoundedIcon sx={{ fontSize: 16 }} />
                          ) : (
                            <ArrowDownwardRoundedIcon sx={{ fontSize: 16 }} />
                          )
                        ) : null}
                      </Box>
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {opt.label}
                      </Typography>
                      {active ? (
                        <Box
                          sx={{
                            minWidth: 18,
                            height: 18,
                            px: 0.5,
                            borderRadius: "50%",
                            bgcolor: theme.palette.primary.main,
                            color: theme.palette.primary.contrastText,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 11,
                            fontWeight: 700,
                            lineHeight: 1,
                          }}
                        >
                          {criterionIndex + 1}
                        </Box>
                      ) : null}
                    </MenuItem>
                  );
                })}
                <Divider sx={{ my: 0.5 }} />
                <MenuItem
                  dense
                  disabled={sortCriteria.length === 0}
                  onClick={() => {
                    handleSortMenuClose();
                    applySortCriteria([]);
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Clear Sorting
                  </Typography>
                </MenuItem>
              </Menu>
            </Box>
          </Box>

          {/* Header Row */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 6fr 1fr 1fr 1fr 128px",
              gap: 1,
              bgcolor: theme.palette.background.default,
              borderBottom: `2px solid ${theme.palette.divider}`,
              width: "100%",
              fontWeight: 700,
            }}
          >
            {renderSortableHeader("Project", "Project")}
            {renderSortableHeader("Date", "Date")}
            {renderSortableHeader("Details", "Details")}
            {renderSortableHeader("Unit", "Unit")}
            {renderSortableHeader("Rate", "Rate")}
            {renderSortableHeader("Total", "Total")}
            <Typography variant="h6" color={theme.palette.primary.main}>
              Actions
            </Typography>
          </Box>

          {/* Items List */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
            }}
          >
            {!invoiceServices || invoiceServices.length === 0 ? (
              <Box sx={{ py: 4, textAlign: "center" }}>
                <Typography variant="body1" color="text.secondary">
                  No services added to this invoice yet.
                </Typography>
              </Box>
            ) : (
              invoiceServices.map((item, index) => (
                <Box
                  key={item.id ?? index}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 6fr 1fr 1fr 1fr 128px",
                    gap: 1,
                    py: 1.5,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  <Typography variant="body2">{item.ProjectNumber}</Typography>
                  <Typography variant="body2">
                    {item.Date ? dayjs(item.Date).format("MM-DD-YYYY") : "N/A"}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {editingItemIndex === index ? (
                      <TextField
                        value={editDetailsText}
                        onChange={(e) => setEditDetailsText(e.target.value)}
                        size="small"
                        multiline
                        fullWidth
                        autoFocus
                      />
                    ) : (
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {item.Details || "N/A"}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="body2">
                    {item.ServiceCategory === "Interpretation"
                      ? (() => {
                          // 2-Hour Minimum: interpretations with
                          // ApplyTwoHourMinimum show at least 2 hours even
                          // when the actual duration is shorter.
                          const rawHours = parseDurationToHours(
                            item.TotalDuration,
                          );
                          if (
                            item.ApplyTwoHourMinimum &&
                            rawHours != null &&
                            rawHours < 2
                          ) {
                            return convertDurationToDecimal("02:00:00");
                          }
                          return convertDurationToDecimal(item.TotalDuration);
                        })()
                      : item.UnitCount && Number(item.UnitCount) > 0
                        ? (() => {
                            // New Unit system: UnitCount + UnitTypeID
                            // (e.g. "330 Pages"). Falls back to WordCount
                            // for legacy services.
                            const unitType = unitTypes.find(
                              (u) => Number(u.id) === Number(item.UnitTypeID),
                            );
                            const unitName = unitType?.Name || "N/A";
                            const count = Number(item.UnitCount);
                            const pluralized =
                              count > 1 && unitName !== "N/A"
                                ? `${unitName}s`
                                : unitName;
                            return `${count.toLocaleString("en-US", {
                              maximumFractionDigits: 0,
                            })} ${pluralized}`;
                          })()
                        : item.WordCount && item.WordCount > 0
                          ? `${item.WordCount} words`
                          : "N/A"}
                  </Typography>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {editingItemIndex === index ? (
                      <TextField
                        value={editRateText}
                        onChange={(e) => setEditRateText(e.target.value)}
                        size="small"
                        fullWidth
                        placeholder="0.00"
                        inputProps={{ style: { textAlign: "right" } }}
                      />
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{ flex: 1, textAlign: "right" }}
                      >
                        {(() => {
                          if (item.ServiceCategory === "Interpretation") {
                            const rate =
                              item.ServiceClientRate !== null &&
                              item.ServiceClientRate !== undefined
                                ? item.ServiceClientRate
                                : item.Client?.ClientRates?.[0]?.Rate;
                            if (rate === 0) return "N/A";
                            return rate !== null && rate !== undefined
                              ? formatAmount(rate)
                              : "N/A";
                          } else {
                            const rate = item.Rate;
                            if (rate === 0) return "N/A";
                            return rate !== null && rate !== undefined
                              ? formatAmount(rate)
                              : "N/A";
                          }
                        })()}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="body2">
                    $
                    {typeof item.AccountCharge === "number"
                      ? item.AccountCharge.toFixed(2)
                      : (parseFloat(item.AccountCharge) || 0).toFixed(2)}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: 0.25,
                    }}
                  >
                    <IconButton
                      size="small"
                      sx={{ width: 30, height: 30, borderRadius: "50%" }}
                      disabled={index === 0}
                      onClick={() => moveService(index, -1)}
                    >
                      <ArrowUpwardRoundedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      sx={{ width: 30, height: 30, borderRadius: "50%" }}
                      disabled={index === invoiceServices.length - 1}
                      onClick={() => moveService(index, 1)}
                    >
                      <ArrowDownwardRoundedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      sx={{ width: 30, height: 30, borderRadius: "50%" }}
                      onClick={() => {
                        if (editingItemIndex === index) {
                          // Save changes
                          setInvoiceServices((prev) => {
                            const updated = [...prev];
                            updated[index].Details = editDetailsText;
                            // Update ServiceClientRate if changed
                            const trimmedRate = editRateText.trim();
                            if (
                              trimmedRate === "" ||
                              trimmedRate.toLowerCase() === "n/a"
                            ) {
                              // Clear custom rate (set to null)
                              updated[index].ServiceClientRate = null;
                            } else {
                              const newRate = parseFloat(
                                trimmedRate.replace(/[^0-9.-]/g, ""),
                              );
                              if (!isNaN(newRate)) {
                                // Allow 0 as a valid rate
                                updated[index].ServiceClientRate = newRate;
                              }
                            }
                            return updated;
                          });
                          setEditingItemIndex(null);
                          setEditDetailsText("");
                          setEditRateText("");
                        } else {
                          // Start editing
                          setEditingItemIndex(index);
                          setEditDetailsText(item.Details || "");
                          // Set current rate for editing
                          const currentRate =
                            item.ServiceCategory === "Interpretation"
                              ? item.ServiceClientRate !== null &&
                                item.ServiceClientRate !== undefined
                                ? item.ServiceClientRate
                                : item.Client?.ClientRates?.[0]?.Rate
                              : item.Rate;
                          setEditRateText(
                            currentRate !== null && currentRate !== undefined
                              ? currentRate.toString()
                              : "N/A",
                          );
                        }
                      }}
                    >
                      {editingItemIndex === index ? (
                        <CheckRoundedIcon fontSize="small" />
                      ) : (
                        <EditRoundedIcon fontSize="small" />
                      )}
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      sx={{ width: 30, height: 30, borderRadius: "50%" }}
                      onClick={() => {
                        const deletedService = invoiceServices[index];
                        setDeletedServices((prev) => [...prev, deletedService]);
                        setInvoiceServices((prev) =>
                          prev.filter((_, i) => i !== index),
                        );
                      }}
                    >
                      <DeleteRoundedIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              ))
            )}
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            flexDirection: "row",
            p: ".75rem 2rem",
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="h5" color={theme.palette.primary.main}>
            Final Total: $
            {(() => {
              // Calculate base total from services
              const serviceTotal = (invoiceServices || []).reduce(
                (sum, item) => sum + (parseFloat(item.AccountCharge) || 0),
                0,
              );

              // If discount fields are set, apply the discount calculation
              if (discountAmount !== undefined || discountPercentage !== null) {
                return calculateDiscountedTotal();
              }

              return serviceTotal;
            })()}
          </Typography>

          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              color="primary"
              variant="outlined"
              size="large"
              endIcon={<DisabledByDefaultRoundedIcon />}
              onClick={() => setOverlayName("")}
              sx={{ borderRadius: "12px" }}
            >
              Discard
            </Button>

            <Button
              disabled={
                isSavingChanges ||
                !invoiceServices ||
                invoiceServices.length === 0
              }
              color="primary"
              variant="contained"
              size="large"
              endIcon={<SaveRoundedIcon />}
              onClick={saveInvoices}
              sx={{ borderRadius: "12px" }}
            >
              {isSavingChanges ? "Saving..." : "Save"}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Edit Lock Dialog */}
      <EditLockDialog
        open={isLocked}
        lockedBy={lockedBy}
        onClose={() => {
          releaseLock();
          setOverlayName("");
        }}
      />
    </Box>
  );
};

export default InvoiceEdit;
